import * as THREE from "three";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";

/** Phase 4 exporters. The geometry only exists in the browser, so the GLB and the PNG views
 *  are made here and posted back to be filed; the Excel and the PDF come from the server. */

const V3 = (a) => new THREE.Vector3(a[0], a[1], a[2]);
const SHOT_BG = 0xf4f6f9;        // a printable background, not the dark stage
const HIGHLIGHT = 0x9f1d1d;

export async function exportGLB(ctrl, { full = true, meta = {} } = {}) {
  const hidden = [];
  if (full) {
    for (const p of Object.values(ctrl.parts)) {
      if (!p.group.visible) { hidden.push(p.group); p.group.visible = true; }
    }
  }
  const root = ctrl.root;
  const prev = root.userData;
  const prevName = root.name;
  root.userData = { ...meta };
  root.name = meta.name || prevName || "design-model";
  try {
    const bin = await new GLTFExporter().parseAsync(root, {
      binary: true, onlyVisible: !full, truncateDrawRange: true,
    });
    return new Blob([bin], { type: "model/gltf-binary" });
  } finally {
    root.userData = prev;
    root.name = prevName;
    hidden.forEach((g) => { g.visible = false; });
  }
}

function poseFor(ctrl, viewId, aspect) {
  const views = ctrl.model.views || [];
  const v = views.find((x) => x.id === viewId) || views[0];
  if (!v) return null;
  const f = aspect < 1 ? 0.95 / Math.sqrt(aspect) : 1;
  const t = V3(v.target || [0, 0, 0]);
  return { pos: t.clone().addScaledVector(V3(v.dir).normalize(), v.distance * f), target: t };
}

function partsBox(ctrl, ids) {
  const box = new THREE.Box3();
  let any = false;
  for (const id of ids || []) {
    const p = ctrl.parts[id];
    if (!p) continue;
    p.group.updateMatrixWorld(true);
    box.union(new THREE.Box3().setFromObject(p.group));
    any = true;
  }
  return any ? box : null;
}

/** Draws the HTML label layer onto the export bitmap, at export resolution. */
function bakeLabels(ctx, ctrl, camera, W, H, { labels, dims }) {
  const scale = Math.max(1, W / 1280);
  const font = 12 * scale;
  ctx.textBaseline = "middle";
  const placed = [];
  const v = new THREE.Vector3();
  for (const L of ctrl.labelOrder || []) {
    const isDim = L.mode === "dims";
    if (isDim ? !dims : !(labels && (L.mode === "both" || L.mode === "normal"))) continue;
    v.copy(L.pos).project(camera);
    if (v.z > 1 || Math.abs(v.x) > 1.02 || Math.abs(v.y) > 1.02) continue;
    const x = (v.x * 0.5 + 0.5) * W;
    const y = (-v.y * 0.5 + 0.5) * H;
    ctx.font = `${isDim ? "" : "600 "}${font}px ui-monospace, "SF Mono", Menlo, monospace`;
    const tw = ctx.measureText(L.text).width;
    const pad = 6 * scale;
    const bw = tw + pad * 2;
    const bh = font + pad * 1.4;
    const bx = x - bw / 2;
    const by = isDim ? y - bh / 2 : y - bh - 11 * scale;
    const box = { l: bx - 2, r: bx + bw + 2, t: by - 2, b: by + bh + 2 };
    if (placed.some((p) => box.l < p.r && box.r > p.l && box.t < p.b && box.b > p.t)) continue;
    placed.push(box);
    ctx.fillStyle = isDim ? "rgba(255,255,255,0.92)" : "rgba(17,24,39,0.9)";
    ctx.strokeStyle = isDim ? "rgba(31,41,55,0.45)" : "rgba(255,255,255,0.18)";
    ctx.lineWidth = 1 * scale;
    const r = 3 * scale;
    ctx.beginPath();
    ctx.roundRect(bx, by, bw, bh, r);
    ctx.fill();
    ctx.stroke();
    if (!isDim) {
      ctx.strokeStyle = "rgba(17,24,39,0.75)";
      ctx.beginPath();
      ctx.moveTo(x, by + bh);
      ctx.lineTo(x, y - 2 * scale);
      ctx.stroke();
    }
    ctx.fillStyle = isDim ? "#1f2937" : "#f8fafc";
    ctx.fillText(L.text, bx + pad, by + bh / 2 + 0.5 * scale);
  }
}

async function shoot(ctrl, { width, height, pose, labels, dims, highlight }) {
  const canvas = document.createElement("canvas");
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, preserveDrawingBuffer: true });
  renderer.setPixelRatio(1);
  renderer.setSize(width, height, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = ctrl.scene;
  const bg = scene.background;
  const fog = scene.fog;
  scene.background = new THREE.Color(SHOT_BG);
  scene.fog = null;

  const camera = new THREE.PerspectiveCamera(ctrl.camera.fov, width / height, 0.1, 500);
  camera.position.copy(pose.pos);
  camera.lookAt(pose.target);
  camera.updateMatrixWorld(true);

  const emissive = [];
  let helper = null;
  if (highlight?.length) {
    for (const id of highlight) {
      const p = ctrl.parts[id];
      if (!p) continue;
      p.mats.forEach((m) => { emissive.push([m, m.emissive.getHex()]); m.emissive.setHex(HIGHLIGHT); });
    }
    const box = partsBox(ctrl, highlight);
    if (box) {
      helper = new THREE.Box3Helper(box.expandByScalar(0.08), new THREE.Color(0xb42318));
      scene.add(helper);
    }
  }
  const dimsWere = ctrl.dimG.visible;
  ctrl.dimG.visible = !!dims;

  renderer.render(scene, camera);
  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const ctx = out.getContext("2d");
  ctx.fillStyle = "#f4f6f9";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(canvas, 0, 0);
  bakeLabels(ctx, ctrl, camera, width, height, { labels, dims });

  ctrl.dimG.visible = dimsWere;
  scene.background = bg;
  scene.fog = fog;
  emissive.forEach(([m, hex]) => m.emissive.setHex(hex));
  if (helper) { scene.remove(helper); helper.geometry.dispose(); helper.material.dispose(); }
  renderer.dispose();

  return new Promise((res) => out.toBlob((b) => res(b), "image/png"));
}

/** One PNG per standard view, at report resolution, with the dimensions baked in. */
export async function renderStandardViews(ctrl, { width = 2400, viewIds } = {}) {
  const ids = viewIds || (ctrl.model.views || []).map((v) => v.id);
  const height = Math.round((width * 9) / 16);
  const out = [];
  for (const id of ids) {
    const pose = poseFor(ctrl, id, width / height);
    if (!pose) continue;
    out.push({
      name: id,
      label: (ctrl.model.views || []).find((v) => v.id === id)?.label || id,
      blob: await shoot(ctrl, { width, height, pose, labels: true, dims: id !== "iso" }),
    });
  }
  return out;
}

/** A view framed on the geometry a check points at, with that part flagged. */
export async function renderCheckView(ctrl, parts, { width = 1600 } = {}) {
  const box = partsBox(ctrl, parts);
  if (!box) return null;
  const height = Math.round((width * 3) / 4);
  const size = box.getSize(new THREE.Vector3());
  const target = box.getCenter(new THREE.Vector3());
  const dir = V3((ctrl.model.views || [])[0]?.dir || [1, 0.55, 1]).normalize();
  const dist = Math.max(size.x, size.y, size.z) * 2.6 + 5;
  return shoot(ctrl, {
    width, height, labels: true, dims: false, highlight: parts,
    pose: { pos: target.clone().addScaledVector(dir, dist), target },
  });
}

export function renderCurrentView(ctrl, { width = 2400 } = {}) {
  const height = Math.round((width * 9) / 16);
  return shoot(ctrl, {
    width, height, labels: ctrl.state.labels, dims: ctrl.state.dims,
    highlight: ctrl.state.sel ? [ctrl.state.sel] : null,
    pose: { pos: ctrl.camera.position.clone(), target: ctrl.controls.target.clone() },
  });
}

export function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
